import Phaser from 'phaser';
import { PALETTE, PRIVACY_URL } from '../config';
import { CloudSync } from '../game/CloudSync';
import { SaveManager } from '../game/SaveManager';
import { t } from '../game/i18n';
import { Button } from '../ui/Button';
import { addBackButton } from '../ui/BackButton';
import { drawGradientBackground } from '../ui/Background';
import { drawPanel } from '../ui/Panel';
import { onResize, viewport } from '../ui/viewport';

/**
 * Account screen: optional Google sign-in for cross-device progress. Guests skip this entirely.
 * Signed-in state shows the account name + a "synced" badge + sign-out; signed-out shows the
 * official Google button (a DOM overlay, like the language combo) plus a short benefit line.
 */
export class Account extends Phaser.Scene {
  private googleBtn?: HTMLDivElement;

  constructor() {
    super('Account');
  }

  create(): void {
    this.build();
    const onCloud = (): void => { this.scene.restart(); };
    SaveManager.instance.on('cloudChanged', onCloud);
    this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => {
      SaveManager.instance.off('cloudChanged', onCloud);
      this.googleBtn?.remove();
    });
    const w0 = this.scale.width, h0 = this.scale.height;
    onResize(this, () => {
      if (Math.abs(this.scale.width - w0) > 2 || Math.abs(this.scale.height - h0) > 2) this.scene.restart();
    });
  }

  private build(): void {
    const { w, h, cx, s } = viewport(this);
    drawGradientBackground(this);
    addBackButton(this, s, () => this.scene.start('MainMenu'));

    this.add.text(cx, h * 0.1, `☁  ${t('account.title')}`, {
      fontFamily: '"Twemoji Country Flags", system-ui', fontSize: `${Math.round(28 * s)}px`, color: '#f4f7ff', fontStyle: 'bold',
    }).setOrigin(0.5).setShadow(0, 3, '#000000aa', 6);

    // Privacy/terms link (always accessible in-game — a portal requirement once accounts exist).
    this.add.text(cx, h - 12 * s, t('legal.privacy'), {
      fontFamily: '"Twemoji Country Flags", system-ui', fontSize: `${Math.max(11, Math.round(12 * s))}px`, color: '#7c88b4',
    }).setOrigin(0.5, 1).setInteractive({ useHandCursor: true })
      .on(Phaser.Input.Events.POINTER_DOWN, () => { try { window.open(PRIVACY_URL, '_blank', 'noopener'); } catch { /* ignore */ } });

    // Profile row: the team name + a rename button (the menu's old "change name" lives here now).
    this.add.text(cx, h * 0.24, `👥 ${SaveManager.instance.teamName}`, {
      fontFamily: '"Twemoji Country Flags", system-ui', fontSize: `${Math.max(18, Math.round(22 * s))}px`, color: '#f4f7ff', fontStyle: 'bold',
    }).setOrigin(0.5).setShadow(0, 2, '#000000aa', 4);
    new Button(this, cx, h * 0.36, `✏️  ${t('menu.changeName')}`, () => this.scene.start('NameEntry', { returnTo: 'Account' }), {
      width: Math.min(260, w * 0.5), height: Math.max(46, 52 * s), color: PALETTE.trackLine, fontSize: Math.max(14, Math.round(16 * s)),
    });

    // Cloud-save panel, lower half.
    const panelW = Math.min(560, w * 0.86);
    const panelH = Math.min(h * 0.42, 300 * s);
    const panelY = h * 0.7;
    drawPanel(this, cx, panelY, panelW, panelH, { radius: 18, fillAlpha: 0.66 });

    if (!SaveManager.instance.cloudEnabled) {
      this.add.text(cx, panelY, t('account.unavailable'), {
        fontFamily: '"Twemoji Country Flags", system-ui', fontSize: `${Math.max(15, Math.round(17 * s))}px`,
        color: '#cfd8f5', align: 'center', wordWrap: { width: panelW - 48 * s },
      }).setOrigin(0.5);
      return;
    }

    if (SaveManager.instance.signedIn) this.buildSignedIn(cx, panelY, panelW, panelH, s);
    else this.buildSignedOut(cx, panelY, panelW, panelH, h, s);
  }

  private buildSignedIn(cx: number, cy: number, panelW: number, panelH: number, s: number): void {
    const user = SaveManager.instance.cloudUser;
    // Show the friendly TEAM name as the identity (the backend username can be auto-generated plumbing).
    const displayName = SaveManager.instance.teamName || user?.name || 'Player';
    this.add.text(cx, cy - panelH * 0.34, '✅', { fontSize: `${Math.round(34 * s)}px` }).setOrigin(0.5);
    this.add.text(cx, cy - panelH * 0.1, t('account.signedInAs'), {
      fontFamily: '"Twemoji Country Flags", system-ui', fontSize: `${Math.max(13, Math.round(14 * s))}px`, color: '#9fb0e0',
    }).setOrigin(0.5);
    this.add.text(cx, cy + panelH * 0.04, displayName, {
      fontFamily: '"Twemoji Country Flags", system-ui', fontSize: `${Math.max(17, Math.round(20 * s))}px`, color: '#f4f7ff', fontStyle: 'bold',
    }).setOrigin(0.5);
    this.add.text(cx, cy + panelH * 0.2, `☁ ${t('account.synced')}`, {
      fontFamily: '"Twemoji Country Flags", system-ui', fontSize: `${Math.max(12, Math.round(13 * s))}px`, color: '#6ef0a0',
    }).setOrigin(0.5);

    new Button(this, cx, cy + panelH * 0.4, t('account.signOut'), () => SaveManager.instance.signOut(), {
      width: Math.min(220, panelW * 0.6), height: Math.max(46, 52 * s), color: PALETTE.trackLine, fontSize: Math.max(14, Math.round(16 * s)),
    });
  }

  private buildSignedOut(cx: number, cy: number, panelW: number, panelH: number, h: number, s: number): void {
    this.add.text(cx, cy - panelH * 0.4, t('account.subtitle'), {
      fontFamily: '"Twemoji Country Flags", system-ui', fontSize: `${Math.max(13, Math.round(15 * s))}px`,
      color: '#cfd8f5', align: 'center', wordWrap: { width: panelW - 56 * s }, lineSpacing: 4,
    }).setOrigin(0.5);

    const googleOn = SaveManager.instance.googleEnabled;
    if (googleOn) {
      // The official Google button is a DOM overlay positioned in the panel's upper half.
      const topPct = Math.round(((cy - panelH * 0.12) / h) * 100);
      const btn = document.createElement('div');
      Object.assign(btn.style, {
        position: 'fixed', left: '50%', top: `${topPct}%`, transform: 'translate(-50%, -50%)',
        zIndex: '45', colorScheme: 'light',
      } satisfies Partial<CSSStyleDeclaration>);
      document.body.appendChild(btn);
      this.googleBtn = btn;
      CloudSync.renderButton(btn);
      this.time.delayedCall(600, () => { if (btn.childElementCount === 0) CloudSync.renderButton(btn); });

      this.add.text(cx, cy + panelH * 0.12, t('auth.or'), {
        fontFamily: '"Twemoji Country Flags", system-ui', fontSize: `${Math.max(12, Math.round(13 * s))}px`, color: '#8893b8',
      }).setOrigin(0.5);
    }

    // Local account (username + password) — opens a keyboard-safe form scene.
    new Button(this, cx, cy + panelH * (googleOn ? 0.32 : 0.05), `👤  ${t('auth.username')} / ${t('auth.password')}`, () => this.scene.start('LocalAuth'), {
      width: Math.min(320, panelW * 0.82), height: Math.max(48, 54 * s), color: PALETTE.cadence, fontSize: Math.max(13, Math.round(16 * s)),
    });
  }
}
